/**
 * Channel mix — first-touch attribution.
 *
 * Backend shape:
 *   { channel_mix: Array<{ first_channel: string | null, n: number }> }
 * sourced from metrics.attribution_by_channel.
 */
import { formatNumber } from '@/lib/utils';

interface Row {
  first_channel: string | null;
  n: number;
}

export function ChannelMixCard({ data }: { data: { channel_mix: Row[] } }) {
  const rows = data.channel_mix ?? [];
  const top = rows[0]?.n || 1;
  return (
    <section className="bg-card border rounded-lg p-6">
      <h3 className="text-lg font-semibold mb-1">Channel mix (first-touch)</h3>
      <p className="text-xs text-muted-foreground mb-4">
        Signups grouped by their first attributed marketing channel.
      </p>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No channel-attributed signups yet. Source: metrics.attribution_by_channel.
        </p>
      ) : (
        <table className="w-full text-sm">
          <tbody>
            {rows.map((r) => {
              const pct = top > 0 ? (r.n / top) * 100 : 0;
              return (
                <tr key={r.first_channel ?? '__unattributed'} className="border-b last:border-0">
                  <td className="py-2 pr-3 w-[30%]">
                    {r.first_channel ?? (
                      <span className="text-muted-foreground italic">Other / unattributed</span>
                    )}
                  </td>
                  <td className="py-2 pr-3">
                    <div className="bg-muted rounded h-2.5 relative overflow-hidden">
                      <div className="bg-primary h-full" style={{ width: `${pct}%` }} />
                    </div>
                  </td>
                  <td className="py-2 pr-3 text-right w-[20%] font-mono">{formatNumber(r.n)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}
